import React, { useState } from "react";
import { useGetDemoRequestsQuery } from "../../../store/auth/authApi";
import { Pagination } from "../../../components";

function formatDate(isoString) {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoString;
  }
}

function formatMode(mode) {
  if (!mode) return "—";
  return String(mode).replace(/_/g, " ");
}

function TableSkeleton() {
  const cols = 9;
  return (
    <table className="min-w-full divide-y divide-gray-200 border-collapse">
      <thead className="bg-gray-50">
        <tr>
          {Array.from({ length: cols }).map((_, i) => (
            <th
              key={i}
              className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
            >
              <div className="h-3 bg-gray-200 rounded w-20 animate-pulse" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-100">
        {Array.from({ length: 8 }).map((_, rowIndex) => (
          <tr key={rowIndex}>
            {Array.from({ length: cols }).map((_, colIndex) => (
              <td key={colIndex} className="px-4 py-3">
                <div className="h-4 bg-gray-100 rounded w-24 animate-pulse" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function DemoRequestsList() {
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState("PENDING");

  const {
    data: response,
    isLoading,
    error,
  } = useGetDemoRequestsQuery({ page: currentPage, status });

  const nested = response?.data;
  const rows = Array.isArray(nested?.data)
    ? nested.data
    : Array.isArray(nested)
      ? nested
      : [];
  const totalPages = response?.lastPage ?? 1;
  const hasMorePages = response?.hasMorePages ?? false;
  const hasPrevPage = currentPage > 1;

  const handleStatusChange = (next) => {
    setStatus(next);
    setCurrentPage(1);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <p className="text-red-600">
            {error?.data?.message ||
              error?.message ||
              "Failed to load demo requests."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100">
        <h1 className="text-2xl font-bold text-[#45464e] mb-2">Demo requests</h1>
        <p className="text-gray-500 text-sm mb-6">
          Submissions from the marketing site demo form. Follow up by email or phone using the
          details below.
        </p>

        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-[#8B313A] focus:border-[#8B313A]"
            >
              <option value="PENDING">Pending</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-100">
          {isLoading ? (
            <TableSkeleton />
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-gray-500 text-sm">
              No demo requests in this filter.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organisation
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Members
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mode
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                      {row.fullname || "—"}
                      <div className="mt-1">
                        <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800">
                          {row.status || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.org_name || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 break-all max-w-[11rem]">
                      {row.email || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {row.mobile_number || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-[10rem]">
                      {row.location || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right">
                      {row.number_of_members != null ? row.number_of_members : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 capitalize">
                      {formatMode(row.mode_of_demo)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {[row.date_1, row.date_2].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {formatDate(row.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {rows.length > 0 && (
          <div className="mt-4 flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              hasNextPage={hasMorePages}
              hasPrevPage={hasPrevPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
